// TODO(hewitt): Should be based on household member - see comments on isOrgAdmin in server_api.ts
import { dateFromString, DateRange, DateString } from './date_string.ts';
import * as md5 from './md5.ts';
import {
  BoundSongListEntry,
  EventSongList,
  Hashed,
  HashesChangedMessage,
  hashLength,
  HymnOfTheMonthSongList,
  Interval,
  IntervalSongList,
  IntervalType,
  Org,
  OrgAddress,
  OrganizationKey,
  OrgId,
  OrgType,
  SocketMessage,
  SocketMessageType,
  SongList,
  SongListEntry,
  SongListSchema,
  SongListType,
  UnboundSongListEntry,
  UpdateWeeklySongListMessage,
} from './model.ts';
import { australianStateCodes, canadianProvinceCodes, isoCountryCodes, usStateCodes } from './org_address_codes.ts';
import { ensureUnreachable } from './util.ts';

// the fields of an OrgAddress, in Stripe's order
const orgAddressFields = ['line1', 'line2', 'city', 'state', 'postalCode', 'country'] as const;

// true only for the assigned ISO 3166-1 alpha-2 country codes (uppercase, e.g. 'US', 'CA', 'GB')
export function isIso2Country(country: string): boolean {
  return isoCountryCodes.has(country);
}

// countries where our orgs genuinely have states/provinces: state is required and must be one of the
// standard codes.  `name` and `example` are quoted back to callers (REST and MCP) in error messages.
const orgStateRules: ReadonlyMap<string, { codes: ReadonlySet<string>; name: string; example: string }> = new Map([
  ['US', { codes: usStateCodes, name: 'US state code', example: "'TX' or 'DC'" }],
  ['CA', { codes: canadianProvinceCodes, name: 'Canadian province or territory code', example: "'ON' or 'PE'" }],
  ['AU', { codes: australianStateCodes, name: 'Australian state or territory code', example: "'NSW' or 'TAS'" }],
]);

// the state/province rule for a country, or undefined where state is optional free text
export function getOrgStateRule(country: string) {
  return orgStateRules.get(country);
}

// an address normalized for storage: every string field trimmed, and fields left empty (or not
// strings at all, from untyped payloads) dropped entirely.  Values are never invented, so a required
// field that arrives blank stays absent and is caught by isValidOrgAddress.
export function normalizeOrgAddress(address: OrgAddress): OrgAddress {
  const normalized: Partial<OrgAddress> = {};
  for (const field of orgAddressFields) {
    const value = address[field];
    const trimmed = typeof value === 'string' ? value.trim() : undefined;
    if (trimmed) {
      normalized[field] = trimmed;
    }
  }
  return normalized as OrgAddress;
}

// an org whose addresses are normalized for storage (a new object - the caller's payload is left
// alone).  The upsert path normalizes before validating, so callers may send untrimmed values but
// only trimmed values are ever stored.
export function normalizeOrgUpsertAddresses<T extends Pick<Partial<Org>, 'physicalAddress' | 'billingAddress'>>(
  org: T,
): T {
  return {
    ...org,
    ...(org.physicalAddress && { physicalAddress: normalizeOrgAddress(org.physicalAddress) }),
    ...(org.billingAddress && { billingAddress: normalizeOrgAddress(org.billingAddress) }),
  };
}

// a usable org address (normalize it first - validation requires trimmed values): a non-empty city
// and an assigned ISO 3166-1 alpha-2 country; US/CA/AU additionally require a standard state or
// province code, while elsewhere state stays optional free text.  Any present field must be a
// non-empty, trimmed string (never '' placeholders).
export function isValidOrgAddress(address: OrgAddress | undefined): address is OrgAddress {
  if (!address?.city || !isIso2Country(address.country ?? '')) {
    return false;
  }
  const stateRule = getOrgStateRule(address.country);
  if (stateRule && !(address.state && stateRule.codes.has(address.state))) {
    return false;
  }
  return orgAddressFields.every((field) => {
    const value = address[field];
    return value === undefined || (typeof value === 'string' && value !== '' && value.trim() === value);
  });
}

// display location computed from a physical address: city, then state when present, then a
// country suffix only when outside the US ('GB' displayed as 'UK')
// e.g. 'Visalia CA', 'Edmonton AB CA', 'Swansea UK', 'Nairobi KE'
// this policy is mirrored by the SQL function org_generated_location - keep the two in sync
export function generatedOrgLocation(address: OrgAddress): string {
  let location = address.city;
  if (address.state) {
    location += ' ' + address.state;
  }
  if (address.country !== 'US') {
    location += address.country === 'GB' ? ' UK' : ' ' + address.country;
  }
  return location;
}

// the location displayed for an org: the override when set, else computed from the physical address
// (empty string for legacy orgs that somehow lack a physicalAddress)
export function effectiveOrgLocation(org: Pick<Org, 'locationOverride' | 'physicalAddress'>): string {
  if (org.locationOverride) {
    return org.locationOverride;
  }
  if (!org.physicalAddress) {
    return '';
  }
  return generatedOrgLocation(org.physicalAddress);
}

// what every stored org address must supply - spelled out because these messages surface to REST and
// MCP callers, who otherwise have no way to know which code sets we accept
const orgAddressRequirements =
  'a city, an ISO 3166-1 alpha-2 country code (e.g. US, CA, GB), and - for US, CA and AU - a standard ' +
  "state or province code (e.g. 'TX', 'ON', 'NSW'); values are trimmed on save";

// creation requires a valid physicalAddress; any address present (create or update) must be valid.
// Normalize addresses (normalizeOrgUpsertAddresses) before validating - validation requires trimmed values.
export function validateOrgUpsertAddresses(org: Pick<Partial<Org>, 'id' | 'physicalAddress' | 'billingAddress'>) {
  if (!org.id && !isValidOrgAddress(org.physicalAddress)) {
    throw new Error(`Org creation requires a physicalAddress with ${orgAddressRequirements}`);
  }
  for (const address of [org.physicalAddress, org.billingAddress]) {
    if (address && !isValidOrgAddress(address)) {
      throw new Error(`Org addresses require ${orgAddressRequirements}`);
    }
  }
}

export function getNameForOrgType(type?: OrgType): string {
  if (!type) {
    return 'Church';
  }
  switch (type) {
    case OrgType.School:
      return 'School';
    case OrgType.Church:
      return 'Church';
    case OrgType.SmallGroup:
      return 'Group';
    default:
      ensureUnreachable(type);
  }
}

export function getWeeklySongListType(church?: Pick<Org, 'type'>) {
  return church?.type === OrgType.Church ? SongListType.WorshipService : SongListType.Interval;
}

export function wasEventSongListEdited(songList?: EventSongList): boolean {
  return Boolean(songList && (songList.name || songList.time || songList.songs.length > 0)); // ignores date
}

export function isBound(entry: SongListEntry | undefined): entry is BoundSongListEntry {
  return Boolean(entry && (entry as any).slug);
}

export function isUnbound(entry: SongListEntry | undefined): entry is UnboundSongListEntry {
  return Boolean(entry && (entry as any).text);
}

// a hymn of the month list holds at most one song, at row 1 - readers ignore the row, but the planner's cell
// reads row 1 only. Prefers the entry at row 1 (what the admin just picked in the planner), otherwise the
// lowest-row entry, and moves it to row 1 (issue #3609)
export function normalizeHymnOfTheMonthSongs(songs: SongListEntry[]): SongListEntry[] {
  const kept = songs.find((entry) => entry.row === 1) ?? [...songs].sort((a, b) => a.row - b.row)[0];
  if (!kept) {
    return [];
  }
  return [{ ...kept, row: 1 }];
}

export function getOrgId(organizationKey: OrganizationKey): OrgId {
  const id = organizationKey.match(/^(?<id>\d+)/)?.groups?.id;
  if (!id) {
    throw new Error(`Unable to parse ID from organization key: ${organizationKey}`);
  }
  return Number(id);
}

export function computeAggregateHash(hashes: Hashed[]): string {
  let content = hashes.reduce((acc, { hash }) => acc + hash, '');
  return computeHash(content);

  // alternatively, we could use the built-in browser sha-1 encoder (no built-in md5)
  // const encoder = new TextEncoder();
  // const data = encoder.encode(content);
  // const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  // const hashArray = Array.from(new Uint8Array(hashBuffer));
  // const hashHex = hashArray
  //   .map((b) => b.toString(16).padStart(2, "0"))
  //   .join(""); // convert bytes to hex string
  // return hashHex.substring(0, hashLength);
}

export function computeHash(content: string): string {
  return md5.md5(content).substring(0, hashLength);
}

export function isUpdateWeeklySongListMessage(message: any): message is UpdateWeeklySongListMessage {
  return Boolean(message && (message as SocketMessage).type === SocketMessageType.UpdateWeeklySongLists);
}

export function isHeartbeatMessage(message: any): message is SocketMessage {
  return Boolean(message && (message as SocketMessage).type === SocketMessageType.Heartbeat);
}

export function isHashesChangedMessage(message: any): message is HashesChangedMessage {
  return Boolean(message && (message as SocketMessage).type === SocketMessageType.HashesChanged);
}

export function hasInterval(songList: SongList): songList is IntervalSongList | HymnOfTheMonthSongList {
  return songList.type === SongListType.Interval || songList.type === SongListType.HymnOfTheMonth;
}

export function getSongListStartDate(songList: SongList): DateString {
  return hasInterval(songList) ? songList.interval.start : songList.date;
}

export function getSongListEndDate(songList: SongList): DateString {
  return hasInterval(songList) ? songList.interval.end : songList.date;
}

export function getSongListDateRange(songList: SongList): DateRange {
  return hasInterval(songList) ? songList.interval : { start: songList.date, end: songList.date };
}

export function getMaxIntervalInDays(type: IntervalType) {
  switch (type) {
    case IntervalType.Week:
      return 7;
    case IntervalType.Month:
      return 31;
    case IntervalType.Year:
      return 365;
    case IntervalType.Custom:
    case IntervalType.Fixed:
    case IntervalType.Season:
      throw new Error(`No maximum for interval ${type}`);
    default:
      ensureUnreachable(type);
  }
}

export function validateStartDate(interval: Interval) {
  const startDate = dateFromString(interval.start);
  switch (interval.type) {
    case IntervalType.Week:
      if (startDate.getDay() !== 0) {
        throw new Error('Week intervals must start on Sunday');
      }
      break;
    case IntervalType.Month:
      if (startDate.getDate() !== 1) {
        throw new Error('Month intervals must start on the first day of the month');
      }
      break;
    case IntervalType.Year:
      if (startDate.getMonth() !== 0 || startDate.getDate() !== 1) {
        throw new Error('Year intervals must start on the first day of the year');
      }
      break;
    case IntervalType.Custom:
    case IntervalType.Season:
    case IntervalType.Fixed:
      break;
    default:
      ensureUnreachable(interval.type);
  }
}

export function isMonthlySongList(songList: SongList): songList is IntervalSongList | HymnOfTheMonthSongList {
  return (
    songList.type === SongListType.HymnOfTheMonth ||
    (hasInterval(songList) && songList.interval.type === IntervalType.Month)
  );
}

export function sortSongListSchemaRows(schema: SongListSchema): SongListSchema {
  return {
    ...schema,
    rows: [...schema.rows].sort((lhs, rhs) => lhs.row - rhs.row),
  };
}
